import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import validateSlices from '../../utils/validateSlices';

import ConfigSelectionStep from './ConfigSelectionStep';
import SliceSelectionStep from './SliceSelectionStep';
import { duplicateSlicesToConfigs, saveConfigs } from './utils';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/shadcn/components/ui/Dialog';

/**
 * Dialog for duplicating slices to storefront configurations
 *
 * @param {Function} onOpenChange - Callback function to handle dialog open/close
 * @param {boolean} open - Whether the dialog is open
 * @param {Object} sourceConfig - The source configuration to duplicate slices from
 * @param {Object} [sourceSlice] - Optional source slice to include within the initial selection
 */
export default function DuplicateSlicesDialog({ onOpenChange, open, sourceConfig, sourceSlice }) {
    const [step, setStep] = useState('slices');
    const [selectedSlices, setSelectedSlices] = useState([]);
    const [selectedConfigs, setSelectedConfigs] = useState([]);
    const [duplicationLoading, setDuplicationLoading] = useState(false);

    useEffect(() => {
        if (open) {
            const enrichedSourceSlice = sourceSlice
                ? { ...sourceSlice, valid: validateSlices([sourceSlice]).length === 0 }
                : null;

            setStep('slices');
            setSelectedSlices(enrichedSourceSlice ? [enrichedSourceSlice] : []);
            setSelectedConfigs([]);
        }
    }, [open, sourceSlice]);

    const toggleSlice = (slice) => {
        setSelectedSlices((prev) => {
            let updatedSlices = [];
            const isSelected = prev.find((s) => s.id === slice.id);

            if (!isSelected) {
                updatedSlices = [...prev, slice];
            } else {
                updatedSlices = prev.filter((s) => s.id !== slice.id);
            }

            return updatedSlices.sort(
                (a, b) =>
                    sourceConfig.slices.findIndex((s) => s.id === a.id) -
                    sourceConfig.slices.findIndex((s) => s.id === b.id),
            );
        });
    };

    const toggleConfig = (config) => {
        const isSelected = selectedConfigs.find((c) => c.id === config.id);
        setSelectedConfigs(
            isSelected ? selectedConfigs.filter((c) => c.id !== config.id) : [...selectedConfigs, config],
        );
    };

    const handleConfirm = async () => {
        setDuplicationLoading(true);
        const loadingToastId = toast.loading('Duplicating slices...');

        try {
            const updatedConfigs = await duplicateSlicesToConfigs(selectedSlices, selectedConfigs);
            await saveConfigs(updatedConfigs);
            onOpenChange();
            toast.success('Slices duplicated successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to duplicate slices');
        } finally {
            setDuplicationLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    const enrichedSlices = sourceConfig?.slices?.map((slice) => ({
        ...slice,
        valid: validateSlices([slice]).length === 0,
    }));

    const hasInvalidSelections =
        selectedSlices.some((slice) => !slice.valid) && selectedConfigs.some((config) => config.published);

    return (
        <Dialog onOpenChange={onOpenChange} open={open}>
            <DialogContent className="flex flex-col max-h-[95vh] sm:max-w-6xl">
                <DialogHeader>
                    <DialogTitle>Duplicate Slices</DialogTitle>
                    <DialogDescription>
                        This action will duplicate the selected slices and add them to the beginning of the selected
                        storefront configurations.
                    </DialogDescription>
                </DialogHeader>
                {step === 'slices' && (
                    <SliceSelectionStep
                        onCancel={onOpenChange}
                        onContinue={() => setStep('configs')}
                        selectedSlices={selectedSlices}
                        slices={enrichedSlices}
                        toggleSlice={toggleSlice}
                    />
                )}
                {step === 'configs' && (
                    <ConfigSelectionStep
                        duplicationLoading={duplicationLoading}
                        hasInvalidSelections={hasInvalidSelections}
                        onBack={() => setStep('slices')}
                        onCancel={onOpenChange}
                        onConfirm={handleConfirm}
                        selectedConfigs={selectedConfigs}
                        toggleConfig={toggleConfig}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
